import { FunctionComponent } from "react";

import { DATE_FORMATS, DateTime } from "@bps/date-time";
import {
  DefaultButton,
  DetailsList,
  DetailsListLayoutMode,
  IColumn,
  SelectionMode,
  Spinner,
  Stack,
  Text
} from "@bps/fluent-ui";
import { MfaMethodDto } from "@libs/api/gateways/bp-id/bp-id.dtos";
import { useRemoveMfaMethod } from "@libs/api/gateways/bp-id/bp-id.hooks";
import { usePermissionsCheckQuery } from "@libs/api/gateways/env/env-gateway.hooks";
import { Permissions } from "@libs/permissions/permissions.enum";

interface MfaMethodsTableProps {
  bpIdUserId: string;
  methods: MfaMethodDto[];
  refresh: () => void;
}

// Maps the stored MFA method key to the value the remove endpoint expects.
// Keep in sync with MfaMethodDisplay (tenant screen).
const removeMethodMap: Record<string, string> = {
  sms: "phone",
  email: "alternateEmail",
  authenticator: "totp"
};

const formatDateSet = (updatedDateTime?: string) => {
  if (!updatedDateTime) return "-";
  return DateTime.fromISO(updatedDateTime, { zone: "utc" })
    .setZone(DateTime.now().timeZone)
    .toFormat(DATE_FORMATS.DAY_DEFAULT_FORMAT);
};

const RemoveMfaMethodButton: FunctionComponent<{
  bpIdUserId: string;
  method: MfaMethodDto;
  refresh: () => void;
}> = ({ bpIdUserId, method, refresh }) => {
  const { mutateAsync: removeMfaRequest, isLoading } = useRemoveMfaMethod();

  const onRemove = async () => {
    const confirmed = confirm(
      `Are you sure you want to remove the MFA method "${method.name}" ?`
    );
    if (!confirmed) return;

    await removeMfaRequest({
      bpIdUserId,
      mfaMethod: removeMethodMap[method.mfaMethod]
    });
    refresh();
  };

  return (
    <DefaultButton onClick={onRemove}>
      {isLoading ? <Spinner /> : "Remove method"}
    </DefaultButton>
  );
};

export const MfaMethodsTable: FunctionComponent<MfaMethodsTableProps> = ({
  bpIdUserId,
  methods,
  refresh
}) => {
  const { data: hasDeletePermission } = usePermissionsCheckQuery(
    Permissions.PltBpidMfaDelete
  );

  const columns: IColumn[] = [
    {
      key: "method",
      name: "Method",
      minWidth: 140,
      maxWidth: 220,
      onRender: (m: MfaMethodDto) => <Text>{m.name}</Text>
    },
    {
      key: "value",
      name: "Value",
      minWidth: 160,
      maxWidth: 300,
      isMultiline: true,
      onRender: (m: MfaMethodDto) => <Text>{m.value || "-"}</Text>
    },
    {
      key: "dateSet",
      name: "Date set",
      minWidth: 120,
      maxWidth: 180,
      onRender: (m: MfaMethodDto) => (
        <Text>{formatDateSet(m.updatedDateTime)}</Text>
      )
    }
  ];

  if (hasDeletePermission) {
    columns.push({
      key: "actions",
      name: "",
      minWidth: 140,
      maxWidth: 160,
      onRender: (m: MfaMethodDto) => (
        <RemoveMfaMethodButton
          bpIdUserId={bpIdUserId}
          method={m}
          refresh={refresh}
        />
      )
    });
  }

  return (
    <Stack styles={{ root: { maxWidth: 1200 } }}>
      <DetailsList
        items={methods}
        columns={columns}
        selectionMode={SelectionMode.none}
        layoutMode={DetailsListLayoutMode.justified}
        getKey={(m: MfaMethodDto) => m.mfaMethod}
      />
    </Stack>
  );
};
